import { inject, Injectable } from '@angular/core';
import { catchError, map, of } from 'rxjs';

import { AUTH_ROUTES } from '@ninety/platform/models/constants/auth-routes';
import { DataLayerService } from '@ninety/platform/services/services/google-analytics/data-layer.service';
import { ErrorTagService } from '@ninety/platform/services/services/google-analytics/error-tag.service';
import { NavigationTagService } from '@ninety/platform/services/services/google-analytics/navigation-tag.service';
import { TokenService } from '@ninety/platform/services/services/token.service';

import { FeatureFlagActions } from '../../../app-entities/feature-flag/feature-flag-state.actions';
import { UserListStateActions } from '../../../app-entities/user-list/user-list-state.actions';
import { UsersStateActions } from '../../../app-entities/users/users-state.actions';
import { appActions } from '../../../app.actions';
import { BaseEffects } from '../../../base.effects';
import { CompanyActions } from '../../company/company-state.actions';
import { CurrentPersonStateActions } from '../../current-person/current-person.actions';
import { EntitlementsStateActions } from '../../entitlements/entitlements-state.actions';
import { HelpfulPermissionsActions } from '../../helpful-permissions/helpful-permissions.actions';
import { LanguageActions } from '../../language/language.actions';
import { LogOutActions } from '../logout/logout.actions';

@Injectable()
export class BootstrapEffects extends BaseEffects {
  private readonly dataLayerService = inject(DataLayerService);
  private readonly navigationTagService = inject(NavigationTagService);
  private readonly errorTagService = inject(ErrorTagService);
  private readonly tokenService = inject(TokenService);

  bootstrap$ = this.mapEffect({
    action: appActions.bootstrap,
    mapFn: () => {
      const path = window.location.pathname.toLowerCase();

      // Snapshotted at bootstrap. The router may rewrite the URL before noTokenFound$ resolves
      // (e.g. the /partner → /login/partner-sign-up vanity redirect), so re-reading pathname
      // in the refresh-failure handler would flip the decision and force a logout.
      const isAuthRoute = Object.values(AUTH_ROUTES).some(route => path.includes(route));
      if (isAuthRoute && !this.tokenService.getAccessToken()) {
        // Named action used instead of noop() for better Redux DevTools traceability.
        return appActions.skipBootstrapOnAuthRoute();
      }

      return this.tokenService.isAccessTokenExpired()
        ? appActions.noTokenFound({ isAuthRoute })
        : appActions.fetchTier1Data();
    },
  });

  fetchTier1Data$ = this.switchMapEffect({
    action: appActions.fetchTier1Data,
    switchMapFn: () =>
      this.http
        .get('/api/v4/FullAppInit', { headers: { 'ninety-origin': location.origin }, withCredentials: true })
        .pipe(
          map(tier1Data => appActions.setTier1Data({ tier1Data })),
          catchError(() => of(LogOutActions.initiateLogOut()))
        ),
  });

  /**
   * Token expired at startup. Refresh first, then continue bootstrap.
   * TokenService deduplicates concurrent refresh calls from guard/bootstrap.
   */
  noTokenFound$ = this.switchMapEffect({
    action: appActions.noTokenFound,
    switchMapFn: ({ isAuthRoute }) =>
      this.tokenService.refreshTokenApi().pipe(
        map(() => appActions.fetchTier1Data()),
        catchError(() =>
          // On a non-auth route the guard handles the redirect to /login?redirectUrl=...
          // Triggering a full logout here would race with and override that redirect,
          // stripping the redirectUrl and breaking deep linking.
          isAuthRoute ? of(LogOutActions.initiateLogOut()) : of(appActions.skipBootstrapOnAuthRoute())
        )
      ),
  });

  loadTier1_company$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { user } }) => CompanyActions.init({ company: user?.company }),
  });

  loadTier1_currentUser$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { user } }) => UsersStateActions.setCurrentUser({ userId: user._id, user }),
  });

  loadTier1_updateFeatureFlagUser$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { user } }) =>
      FeatureFlagActions.updateLaunchDarklyUser({
        currentUserOverride: user,
        personOverride: user?.person,
        companyOverride: user?.company,
      }),
  });

  loadTier1_updateFlags$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { flags } }) => FeatureFlagActions.updatedFlagsProcessed({ flags, fromLd: false }),
  });

  loadTier1_language$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { companyLanguage, defaultBOSLanguage } }) =>
      LanguageActions.init({ companyLanguage, defaultBOSLanguage }),
  });

  loadTier1_loggedInUser$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { user } }) => UserListStateActions.setLoggedInUser({ loggedInUser: user }),
  });

  loadTier1_currentPerson$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { user } }) => CurrentPersonStateActions.setPerson({ person: user?.person }),
  });

  loadTier1_helpful$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: ({ tier1Data: { user } }) =>
      HelpfulPermissionsActions.init({ helpfulPermissions: user?.person.helpfulPermissions }),
  });

  loadTier1_entitlements$ = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: () => EntitlementsStateActions.init(),
  });

  loadTier2 = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: () => appActions.fetchTier2Data(),
  });

  initStateService = this.mapEffect({
    action: appActions.setTier1Data,
    mapFn: () => appActions.initStateService(),
  });

  setupDataLayer = this.tapEffect({
    action: appActions.setTier1Data,
    tapFn: () => {
      this.dataLayerService.init();
      this.errorTagService.init();
      this.navigationTagService.init();
    },
  });
}
